/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印页面图片列表组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-09 17:51:50
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { message, Modal, Button } from 'antd'
import { AutoSizer, List } from 'react-virtualized'
import {
  WatermarkExtractionStatus,
  WatermarkItem
} from '../../context/WatermarkBatchContext'
import { PhotoItem } from '../../context/WatermarkBatchContext'
import { useWatermarkBatchContext } from '../../context/WatermarkBatchContext'
import { useAppDispatch } from '../../../../../app/hooks'
import { setPhotoPickerPhotos } from '../../../../../actions/photoPicker'
import { useWatermarkExtraction } from '../../hooks/useWatermarkExtraction'
import {
  useWatermarkBatchEditorTrack,
  getPageType,
  calculatePhotoStats
} from '../../hooks/useWatermarkBatchEditorTrack'
import FilterTabs, { FilterType } from './FilterTabs'
import ImageItem from './ImageItem'
import ImageListHeader from './ImageListHeader'
import ImageListFooter from './ImageListFooter'
import EmptyState from './EmptyState'
import AddPhotosModal from '../AddPhotosModal'
import FailedPhotosModal from './FailedPhotosModal'
import './ImageList.scss'

const ImageList: React.FC = () => {
  const {
    photoList: photos,
    currentIndex,
    setCurrentIndex,
    currentPhoto,
    setPhotos,
    updatePhotosStatus,
    updatePhotoWatermarkData,
    watermarkCnt,
    setShouldBlockScroll,
    getShouldBlockScroll,
    registerGetSelectedPhotos,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isExtracting,
    setExtracting,
    selectedPhotoSeqs,
    setSelectedPhotoSeqs,
    checkAll,
    setCheckAll
  } = useWatermarkBatchContext()
  const dispatch = useAppDispatch()
  const [filterType, setFilterType] = useState<FilterType>('all')
  const { extractWatermarks } = useWatermarkExtraction()
  const [addPhotosVisible, setAddPhotosVisible] = useState(false)
  const [failedPhotosVisible, setFailedPhotosVisible] = useState(false)

  // 多选状态管理（保留 unCheckedPhotos 用于全选模式下的反向选择逻辑）
  const [unCheckedPhotos, setUnCheckedPhotos] = useState<Set<number>>(new Set())

  // 格式化数字
  const formatNum = useCallback((num: number): string => {
    if (num >= 10000) {
      return `${(num / 10000).toFixed(1)}万`
    }
    return num.toString()
  }, [])

  // 切换单个照片的选中状态
  const handleTogglePhoto = useCallback(
    (photo: PhotoItem, e: React.MouseEvent) => {
      e.stopPropagation() // 阻止触发照片选择事件

      // 点击 checkbox 选中时，禁止滚动
      setShouldBlockScroll(true)

      const photoSeq = photo.seq
      const isCurrentlyChecked = checkAll
        ? !unCheckedPhotos.has(photoSeq)
        : selectedPhotoSeqs.has(photoSeq)

      if (checkAll) {
        // 全选状态下，点击照片会将其加入未选中列表
        if (isCurrentlyChecked) {
          setUnCheckedPhotos((prev) => {
            const newSet = new Set(prev)
            newSet.add(photoSeq)
            return newSet
          })
          // 同时从 Context 的选中状态中移除
          setSelectedPhotoSeqs((prev) => {
            const newSet = new Set(prev)
            newSet.delete(photoSeq)
            return newSet
          })
        } else {
          setUnCheckedPhotos((prev) => {
            const newSet = new Set(prev)
            newSet.delete(photoSeq)
            return newSet
          })
          // 同时添加到 Context 的选中状态
          setSelectedPhotoSeqs((prev) => {
            const newSet = new Set(prev)
            newSet.add(photoSeq)
            return newSet
          })
        }
      } else {
        // 非全选状态下，点击照片会切换其选中状态
        setSelectedPhotoSeqs((prev) => {
          const newSet = new Set(prev)
          if (newSet.has(photoSeq)) {
            newSet.delete(photoSeq)
          } else {
            newSet.add(photoSeq)
          }
          return newSet
        })
      }
    },
    [
      checkAll,
      selectedPhotoSeqs,
      unCheckedPhotos,
      setShouldBlockScroll,
      setSelectedPhotoSeqs
    ]
  )

  // 判断照片是否被选中
  const isPhotoChecked = useCallback(
    (photo: PhotoItem): boolean => {
      if (checkAll) {
        return !unCheckedPhotos.has(photo.seq)
      }
      return selectedPhotoSeqs.has(photo.seq)
    },
    [checkAll, selectedPhotoSeqs, unCheckedPhotos]
  )

  // 使用埋点 hook
  const { trackWatermarkBatchEditor } = useWatermarkBatchEditorTrack()

  // 计算照片统计
  const {
    allPhotoCount,
    uneditedCount,
    editedCount,
    failedExtractionCount
  } = calculatePhotoStats(photos)

  const listRef = useRef<List | null>(null)

  // 判断照片是否处于提取中状态
  const isPhotoExtracting = useCallback((photo: PhotoItem) => {
    // 只有watermarkStatus为EXTRACTING的照片才显示提取中状态
    return photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING
  }, [])

  // 提取失败照片列表
  const failedPhotos = useMemo(
    () =>
      photos.filter(
        (photo) => photo.watermarkStatus === WatermarkExtractionStatus.FAILED
      ),
    [photos]
  )

  // 默认不展示提取失败照片
  const visiblePhotos = useMemo(
    () =>
      photos.filter(
        (photo) => photo.watermarkStatus !== WatermarkExtractionStatus.FAILED
      ),
    [photos]
  )

  const {
    allPhotoCount: visibleAllCount,
    uneditedCount: visibleUneditedCount,
    editedCount: visibleEditedCount,
    failedExtractionCount: visibleFailedCount
  } = calculatePhotoStats(visiblePhotos)

  // 根据筛选类型过滤照片 - 使用 useMemo 优化性能
  const filteredPhotos = useMemo(() => {
    return visiblePhotos.filter((photo) => {
      const isEditFailed =
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_FAILED
      const isExtractingTimeout =
        photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING_TIMEOUT
      const isPhotoExtractingStatus = isPhotoExtracting(photo)

      switch (filterType) {
        case 'unedited':
          return (
            isPhotoExtractingStatus ||
            photo.watermarkStatus === undefined ||
            photo.watermarkStatus === WatermarkExtractionStatus.COMPLETED ||
            photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES
          )
        case 'edited':
          // 已编辑筛选包含编辑完成的照片
          return (
            photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
          )
        case 'failed':
          // 异常筛选包含编辑失败和提取超时的照片（提取失败已移除）
          return isEditFailed || isExtractingTimeout
        default:
          return true
      }
    })
  }, [visiblePhotos, filterType, isPhotoExtracting])

  // 创建 smallURL 到索引的映射，避免在 map 中重复查找 - 使用 useMemo 优化
  const photoIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    photos.forEach((photo, index) => {
      map.set(photo.smallURL, index)
    })
    return map
  }, [photos])

  // 计算照片数量（基于筛选后的照片）
  const photosNum = filteredPhotos.length

  // 计算已选中的照片数量（基于筛选后的照片）
  const selectedCount = useMemo(() => {
    if (checkAll) {
      // 全选状态下，计算筛选后照片中未被排除的数量
      const filteredSeqs = new Set(filteredPhotos.map((photo) => photo.seq))
      const filteredUnCheckedCount = Array.from(unCheckedPhotos).filter((seq) =>
        filteredSeqs.has(seq)
      ).length
      return filteredPhotos.length - filteredUnCheckedCount
    } else {
      // 非全选状态下，计算筛选后照片中被选中的数量
      const filteredSeqs = new Set(filteredPhotos.map((photo) => photo.seq))
      return Array.from(selectedPhotoSeqs).filter((seq) =>
        filteredSeqs.has(seq)
      ).length
    }
  }, [checkAll, filteredPhotos, unCheckedPhotos, selectedPhotoSeqs])

  // 全选/取消全选（只针对当前筛选类型下的照片）
  const onCheckAll = useCallback(
    (e: any) => {
      const checked = e.target.checked
      const filteredSeqs = new Set(filteredPhotos.map((photo) => photo.seq))

      if (checked) {
        // 全选：只将当前筛选类型下的照片从未选中列表中移除
        setUnCheckedPhotos((prev) => {
          const newSet = new Set(prev)
          filteredSeqs.forEach((seq) => newSet.delete(seq))
          return newSet
        })
        // 将当前筛选类型下的照片加入 Context 的选中列表（如果之前不是全选状态）
        if (!checkAll) {
          setSelectedPhotoSeqs((prev) => {
            const newSet = new Set(prev)
            filteredSeqs.forEach((seq) => newSet.add(seq))
            return newSet
          })
        }
        setCheckAll(true)
      } else {
        // 取消全选：只将当前筛选类型下的照片加入未选中列表
        setUnCheckedPhotos((prev) => {
          const newSet = new Set(prev)
          filteredSeqs.forEach((seq) => newSet.add(seq))
          return newSet
        })
        // 将当前筛选类型下的照片从 Context 的选中列表中移除
        setSelectedPhotoSeqs((prev) => {
          const newSet = new Set(prev)
          filteredSeqs.forEach((seq) => newSet.delete(seq))
          return newSet
        })
        setCheckAll(false)
      }
    },
    [filteredPhotos, checkAll, setSelectedPhotoSeqs, setCheckAll]
  )

  // 计算各状态照片数量（使用已有的统计）
  const allCount = visibleAllCount
  const failedCount = visibleFailedCount

  // 只计算提取失败的数量（不包括提取超时和编辑失败）
  const extractionFailedCount = failedPhotos.length

  const prevCurrentIndexRef = useRef<number>(-1)

  // 当 currentIndex 变化时，滚动到对应的图片位置
  // 注意：只有当 currentIndex 真正变化时才滚动，photos 更新时不滚动
  useEffect(() => {
    if (prevCurrentIndexRef.current === currentIndex) {
      return
    }
    prevCurrentIndexRef.current = currentIndex

    if (currentIndex === -1) {
      return
    }

    // 如果禁止滚动（用户手动点击），则不执行滚动
    // 检查后立即重置标记，避免后续 useEffect 执行时误判
    if (getShouldBlockScroll()) {
      setShouldBlockScroll(false)
      return
    }

    const currentPhoto = photos[currentIndex]
    if (!currentPhoto) {
      return
    }

    const filteredIndex = filteredPhotos.findIndex(
      (photo) => photo.smallURL === currentPhoto.smallURL
    )
    if (filteredIndex === -1) {
      return
    }

    const columnCount = 2
    const rowIndex = Math.floor(filteredIndex / columnCount)
    if (listRef.current?.scrollToRow) {
      listRef.current.scrollToRow(rowIndex)
    }
  }, [
    currentIndex,
    photos,
    filteredPhotos,
    getShouldBlockScroll,
    setShouldBlockScroll
  ])

  const handleViewFailedPhotos = useCallback(() => {
    if (failedPhotos.length === 0) {
      message.info('暂无提取失败的照片')
      return
    }
    setFailedPhotosVisible(true)
  }, [failedPhotos.length])

  const handleCloseFailedPhotos = useCallback(() => {
    setFailedPhotosVisible(false)
  }, [])

  const handleSelectImage = useCallback(
    (index: number): void => {
      // 获取当前选中的照片
      const selectedPhoto = photos[index]

      // 如果有未保存的修改，弹窗确认
      if (hasUnsavedChanges) {
        Modal.confirm({
          title: '确认切换照片',
          content:
            '当前编辑内容有未应用的修改，切换照片将放弃当前修改，是否确认？',
          okText: '确认',
          cancelText: '取消',
          onOk: () => {
            // 用户确认放弃修改，重置编辑状态
            setHasUnsavedChanges(false)

            // 埋点：点击照片
            trackWatermarkBatchEditor({
              actionType: 'clickPhoto',
              pageType: getPageType(filterType),
              number1: watermarkCnt,
              number2: allPhotoCount,
              number3: uneditedCount,
              number4: failedExtractionCount,
              photoNum: allPhotoCount,
              WatermarkID: selectedPhoto?.watermarkID
            })

            setCurrentIndex(index)
          }
        })
        return
      }

      // 埋点：点击照片
      trackWatermarkBatchEditor({
        actionType: 'clickPhoto',
        pageType: getPageType(filterType),
        number1: watermarkCnt,
        number2: allPhotoCount,
        number3: uneditedCount,
        number4: failedExtractionCount,
        photoNum: allPhotoCount,
        WatermarkID: selectedPhoto?.watermarkID
      })

      setCurrentIndex(index)
    },
    [
      photos,
      hasUnsavedChanges,
      setHasUnsavedChanges,
      trackWatermarkBatchEditor,
      filterType,
      watermarkCnt,
      allPhotoCount,
      uneditedCount,
      failedExtractionCount,
      setCurrentIndex
    ]
  )

  const openAddPhotosModal = useCallback(() => {
    setAddPhotosVisible(true)
  }, [])

  const closeAddPhotosModal = useCallback(() => {
    setAddPhotosVisible(false)
  }, [])

  // 打开添加照片弹窗
  const handleGoToAllPhotos = useCallback(() => {
    // 计算已编辑数量（编辑完成的照片）
    const editedCount = photos.filter(
      (photo) =>
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
    ).length

    // 分级提示方案：
    // 300-599张：提醒（可以继续）
    // 600-999张：警告（可以继续但更强烈）
    // 1000张及以上：禁止（不能继续）

    if (editedCount >= 1000) {
      // 1000张及以上：禁止继续添加
      Modal.warning({
        title: '禁止添加',
        content: `你当前已编辑数量为${editedCount}张，已达到上限（1000张）。为了系统稳定运行，请先下载保存已编辑的照片，然后再继续添加。`,
        okText: '我知道了',
        onOk: () => {
          // 禁止继续，不执行跳转
        }
      })
      return
    } else if (editedCount >= 600) {
      // 600-999张：警告提示
      Modal.confirm({
        title: '警告',
        content: (
          <div>
            <p>
              你当前已编辑数量为{editedCount}
              张，已接近上限（1000张）。为了避免异常报错，强烈建议您先下载保存已编辑的照片，然后再继续添加。
            </p>
            <p style={{ marginTop: 8, color: '#1890ff' }}>
              提示：您也可以先移除部分照片，移除后可继续添加新照片。
            </p>
          </div>
        ),
        okText: '继续添加',
        cancelText: '取消',
        onOk: () => {
          // 用户选择继续添加，执行跳转
          // 埋点：点击继续添加照片
          trackWatermarkBatchEditor({
            actionType: 'clickAddMore',
            pageType: getPageType(filterType),
            number1: watermarkCnt,
            number2: allPhotoCount,
            number3: uneditedCount,
            number4: failedExtractionCount,
            photoNum: allPhotoCount,
            WatermarkID: currentPhoto?.watermarkID
          })

          openAddPhotosModal()
        },
        onCancel: () => {
          // 用户选择取消，不执行任何操作
        }
      })
      return
    } else if (editedCount >= 300) {
      // 300-599张：提醒提示
      Modal.info({
        title: '提醒',
        content: (
          <div>
            <p>
              你当前已编辑数量为{editedCount}
              张。为了避免异常报错，建议您适时下载保存已编辑的照片，合理控制照片数量。
            </p>
            <p style={{ marginTop: 8, color: '#1890ff' }}>
              提示：您也可以先移除部分照片，移除后可继续添加新照片。
            </p>
          </div>
        ),
        okText: '继续添加',
        onOk: () => {
          // 用户确认后继续添加，执行跳转
          // 埋点：点击继续添加照片
          trackWatermarkBatchEditor({
            actionType: 'clickAddMore',
            pageType: getPageType(filterType),
            number1: watermarkCnt,
            number2: allPhotoCount,
            number3: uneditedCount,
            number4: failedExtractionCount,
            photoNum: allPhotoCount,
            WatermarkID: currentPhoto?.watermarkID
          })

          openAddPhotosModal()
        }
      })
      return
    }

    // 已编辑数量未超过300张，直接跳转
    // 埋点：点击继续添加照片
    trackWatermarkBatchEditor({
      actionType: 'clickAddMore',
      pageType: getPageType(filterType),
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      photoNum: allPhotoCount,
      WatermarkID: currentPhoto?.watermarkID
    })

    openAddPhotosModal()
  }, [
    photos,
    trackWatermarkBatchEditor,
    filterType,
    watermarkCnt,
    allPhotoCount,
    uneditedCount,
    failedExtractionCount,
    currentPhoto,
    openAddPhotosModal
  ])

  // 获取选中的照片列表
  const getSelectedPhotos = useCallback((): PhotoItem[] => {
    if (checkAll) {
      // 全选状态，排除未选中的照片（只考虑筛选后的照片）
      return filteredPhotos.filter((photo) => !unCheckedPhotos.has(photo.seq))
    } else {
      // 非全选状态，只包含选中的照片（只考虑筛选后的照片）
      return filteredPhotos.filter((photo) => selectedPhotoSeqs.has(photo.seq))
    }
  }, [checkAll, filteredPhotos, selectedPhotoSeqs, unCheckedPhotos])

  // 注册获取选中照片的函数到 Context
  useEffect(() => {
    registerGetSelectedPhotos(getSelectedPhotos)
  }, [registerGetSelectedPhotos, getSelectedPhotos])

  // 批量删除选中的照片
  const handleBatchDeletePhotos = useCallback(() => {
    const selectedPhotos = getSelectedPhotos()
    if (selectedPhotos.length === 0) {
      message.warning('请先选择要删除的照片')
      return
    }

    // 显示确认弹窗
    Modal.confirm({
      title: `确认移除${selectedPhotos.length}张照片吗`,
      content: (
        <div>
          <p>移除后已编辑内容不可恢复，可重新添加</p>
          <p style={{ marginTop: 8, color: '#1890ff' }}>
            移除后可继续添加新照片
          </p>
        </div>
      ),
      okText: '移除',
      cancelText: '取消',
      okType: 'danger',
      onOk: () => {
        // 埋点：批量删除照片
        trackWatermarkBatchEditor({
          actionType: 'deletePhoto',
          pageType: getPageType(filterType),
          number1: watermarkCnt,
          number2: allPhotoCount,
          number3: uneditedCount,
          number4: failedExtractionCount,
          photoNum: selectedPhotos.length,
          WatermarkID: currentPhoto?.watermarkID
        })

        // 获取要删除的照片的 seq 集合
        const seqsToDelete = new Set(selectedPhotos.map((photo) => photo.seq))

        // 过滤掉要删除的照片
        const remainingPhotos = photos.filter(
          (photo) => !seqsToDelete.has(photo.seq)
        )

        // 更新照片列表
        setPhotos(remainingPhotos)

        // 同步更新 Redux 中的数据
        dispatch(setPhotoPickerPhotos(remainingPhotos, 'batch-editor'))

        // 调整当前索引：如果当前照片被删除，选择第一个剩余的照片
        if (remainingPhotos.length === 0) {
          // 如果所有照片都被删除，设置为 -1
          setCurrentIndex(-1)
        } else {
          const currentPhotoSeq = photos[currentIndex]?.seq
          if (currentPhotoSeq && seqsToDelete.has(currentPhotoSeq)) {
            // 当前照片被删除，选择第一个剩余的照片
            setCurrentIndex(0)
          } else {
            // 当前照片未被删除，需要找到它在剩余照片中的新索引
            const newIndex = remainingPhotos.findIndex(
              (photo) => photo.seq === currentPhotoSeq
            )
            if (newIndex !== -1) {
              setCurrentIndex(newIndex)
            } else if (currentIndex >= remainingPhotos.length) {
              // 如果当前索引超出范围，设置为最后一个
              setCurrentIndex(remainingPhotos.length - 1)
            }
          }
        }

        // 清空选中状态
        setCheckAll(false)
        setSelectedPhotoSeqs(new Set())
        setUnCheckedPhotos(new Set())

        message.success(`已删除 ${selectedPhotos.length} 张照片`)
      }
    })
  }, [
    getSelectedPhotos,
    photos,
    setPhotos,
    currentIndex,
    setCurrentIndex,
    dispatch,
    trackWatermarkBatchEditor,
    filterType,
    watermarkCnt,
    allPhotoCount,
    uneditedCount,
    failedExtractionCount,
    currentPhoto
  ])

  // 重新提取单张照片
  const handleReExtractSinglePhoto = useCallback(
    (photo: PhotoItem, e: React.MouseEvent) => {
      e.stopPropagation() // 阻止触发照片选择事件

      // 埋点：重新提取单张照片
      trackWatermarkBatchEditor({
        actionType: 'reExtractWatermark',
        pageType: getPageType(filterType),
        number1: watermarkCnt,
        number2: allPhotoCount,
        number3: uneditedCount,
        number4: failedExtractionCount,
        photoNum: 1,
        WatermarkID: photo.watermarkID
      })

      // 创建待提取照片数组
      const pendingPhotos = [
        {
          ...photo,
          watermarkStatus: WatermarkExtractionStatus.EXTRACTING
        }
      ]

      // 定义照片状态更新回调
      const handlePhotosStatusUpdate = (
        photosToUpdate: PhotoItem[],
        status: WatermarkExtractionStatus
      ) => {
        if (status === WatermarkExtractionStatus.FAILED) {
          message.error('重新提取水印失败')
          return
        }
        if (status === WatermarkExtractionStatus.COMPLETED) {
          message.success('重新提取水印成功')
        }
        updatePhotosStatus(photosToUpdate, status)
      }

      // 定义水印数据更新回调
      const handleWatermarkDataUpdate = (
        photoUrl: string,
        watermarkID: string,
        watermarkData: WatermarkItem[]
      ) => {
        const photoIndexInState = photos.findIndex(
          (p) => p.smallURL === photoUrl
        )
        if (photoIndexInState !== -1) {
          const photoInState = photos[photoIndexInState]
          updatePhotoWatermarkData(photoIndexInState, watermarkData)
          const updatedPhoto = {
            ...photoInState,
            watermarkID,
            watermarkStatus: WatermarkExtractionStatus.COMPLETED
          }
          updatePhotosStatus(
            [updatedPhoto],
            WatermarkExtractionStatus.COMPLETED
          )
        }
      }

      // 定义提取完成回调，选中成功提取的照片
      const handleExtractionComplete = (successPhotos: PhotoItem[]) => {
        if (currentIndex !== -1) {
          return
        }
        if (successPhotos.length > 0) {
          const photoIndexInState = photos.findIndex(
            (p) => p.smallURL === successPhotos[0].smallURL
          )
          if (photoIndexInState !== -1) {
            setCurrentIndex(photoIndexInState)
          }
        }
      }

      // 异步执行水印提取
      extractWatermarks(
        pendingPhotos,
        handlePhotosStatusUpdate,
        handleWatermarkDataUpdate,
        handleExtractionComplete,
        setExtracting
          ? (photoCount: number) => setExtracting(true, photoCount)
          : undefined,
        setExtracting ? () => setExtracting(false, 0) : undefined
      )
    },
    [
      currentIndex,
      photos,
      extractWatermarks,
      updatePhotosStatus,
      updatePhotoWatermarkData,
      setCurrentIndex,
      trackWatermarkBatchEditor,
      filterType,
      watermarkCnt,
      allPhotoCount,
      uneditedCount,
      failedExtractionCount,
      setExtracting
    ]
  )

  return (
    <div className="imageList">
      <ImageListHeader
        extractionFailedCount={extractionFailedCount}
        photosCount={visiblePhotos.length}
        filterType={filterType}
        onFilterChange={setFilterType}
        allCount={allCount}
        uneditedCount={visibleUneditedCount}
        editedCount={visibleEditedCount}
        failedCount={failedCount}
        onViewFailedPhotos={handleViewFailedPhotos}
        onTrack={(params) => {
          trackWatermarkBatchEditor({
            actionType: 'switchPageType',
            pageType: params.pageType,
            number1: params.watermarkCnt,
            number2: params.allPhotoCount,
            number3: params.uneditedCount,
            number4: params.failedExtractionCount,
            photoNum: params.photoNum,
            WatermarkID: params.WatermarkID
          })
        }}
        watermarkCnt={watermarkCnt}
        allPhotoCount={allPhotoCount}
        failedExtractionCount={failedExtractionCount}
        currentPhoto={currentPhoto}
      />

      <div className="listContainer">
        {visiblePhotos.length === 0 ? (
          <EmptyState
            onGoToAllPhotos={handleGoToAllPhotos}
            isExtracting={isExtracting}
          />
        ) : (
          <div className="virtualizedWrapper">
            <AutoSizer>
              {({ width, height }) => {
                if (width <= 0 || height <= 0) return null

                const listPadding = 12
                const gutter = 12
                const columnCount = 2
                const innerWidth = Math.max(0, width - listPadding * 2)
                const totalGutter = gutter * (columnCount - 1)
                const columnWidth = Math.max(
                  0,
                  Math.floor((innerWidth - totalGutter) / columnCount)
                )
                const itemHeight = Math.max(0, Math.round(columnWidth * 1.2))
                const rowHeight = itemHeight + gutter
                const rowCount = Math.ceil(filteredPhotos.length / columnCount)

                const rowRenderer = ({
                  index,
                  key,
                  style
                }: {
                  index: number
                  key: string
                  style: React.CSSProperties
                }) => {
                  const startIndex = index * columnCount
                  const items: React.ReactNode[] = []

                  for (let col = 0; col < columnCount; col++) {
                    const photo = filteredPhotos[startIndex + col]
                    if (!photo) break
                    const originalIndex =
                      photoIndexMap.get(photo.smallURL) ?? -1
                    const uniqueKey = `photo-${photo.seq}-${originalIndex}`

                    items.push(
                      <div
                        key={uniqueKey}
                        className="imageListCol"
                        style={{ width: columnWidth }}
                      >
                        <ImageItem
                          photo={photo}
                          originalIndex={originalIndex}
                          currentIndex={currentIndex}
                          isChecked={isPhotoChecked(photo)}
                          isExtracting={isPhotoExtracting(photo)}
                          thumbWidth={columnWidth}
                          onSelect={handleSelectImage}
                          onToggleCheck={handleTogglePhoto}
                          onReExtract={handleReExtractSinglePhoto}
                        />
                      </div>
                    )
                  }

                  return (
                    <div
                      key={key}
                      className="imageListRow"
                      style={{
                        ...style,
                        paddingLeft: listPadding,
                        paddingRight: listPadding,
                        paddingBottom: gutter,
                        boxSizing: 'border-box'
                      }}
                    >
                      <div
                        className="imageListRowInner"
                        style={{ height: itemHeight, gap: gutter }}
                      >
                        {items}
                      </div>
                    </div>
                  )
                }

                return (
                  <List
                    className="virtualized-list"
                    ref={listRef}
                    width={width}
                    height={height}
                    rowCount={rowCount}
                    rowHeight={rowHeight}
                    rowRenderer={rowRenderer}
                    overscanRowCount={6}
                    style={{ outline: 'none', overflowX: 'hidden' }}
                  />
                )
              }}
            </AutoSizer>
          </div>
        )}

        {visiblePhotos.length === 1 && (
          <div className="add-more-tip">
            <span className="add-more-tip__text">
              添加更多照片,批量修改更高效
            </span>
            <Button
              type="link"
              className="add-more-tip__action"
              disabled={isExtracting}
              onClick={handleGoToAllPhotos}
            >
              去添加
            </Button>
          </div>
        )}
      </div>

      {visiblePhotos.length > 0 && (
        <ImageListFooter
          photosNum={photosNum}
          selectedCount={selectedCount}
          checkAll={checkAll}
          selectedCountText={formatNum(selectedCount)}
          isExtracting={isExtracting}
          onCheckAll={onCheckAll}
          onGoToAllPhotos={handleGoToAllPhotos}
          onBatchDelete={handleBatchDeletePhotos}
        />
      )}

      <AddPhotosModal
        visible={addPhotosVisible}
        currentPhoto={currentPhoto}
        onClose={closeAddPhotosModal}
      />

      <FailedPhotosModal
        visible={failedPhotosVisible}
        photos={failedPhotos}
        onClose={handleCloseFailedPhotos}
      />
    </div>
  )
}

export default ImageList
